import reading from './sacramentsPropertiesRows.json';
import type { BuildContext } from '../../types';
import type { PolygonIndex } from '../../../geo/polygonIndex';
import type { Vec2 } from '../../../geo/types';
import { bufferPolyline, difference } from '../../../geo/clip';
import type { ReservedFootprint } from '../types';
import { obliqueRowLots, type ObliqueRow } from './obliqueRowLots';

export const SACRAMENTS_PROPERTY_ROWS = reading.rows as unknown as readonly ObliqueRow[];

/** Street homes and the tentative gasthuis remain separate; later redevelopment is excluded. */
export function planSacramentsProperties(
  ctx: Pick<BuildContext, 'layout' | 'site'>,
  buildable: PolygonIndex,
  reserved: readonly ReservedFootprint[]
) {
  if (!ctx.layout.landmarks.some((l) => l.id === 'landmark-kalverstraat-painted-hall'))
    return { lots: [], reserved: [], unresolved: [] };
  const approach = bufferPolyline(
    reading.approach.map((p) => [p[0]!, p[1]!] as Vec2),
    0.8,
    'butt'
  );
  const access = approach.map((polygon, i) => ({ id: `sacraments-approach-${i}`, polygon }));
  const lots = obliqueRowLots(
    SACRAMENTS_PROPERTY_ROWS,
    ctx.layout,
    buildable,
    [...reserved, ...access],
    51_000
  );
  const built = new Set(lots.map((l) => l.reconstruction!.id));
  const ground = { outer: reading.privateGround.map((p) => [p[0]!, p[1]!] as Vec2), holes: [] };
  const dry = difference(
    [ground, ...approach],
    [
      ...ctx.site.water,
      ...ctx.site.streets,
      ...reserved.map((r) => r.polygon),
      ...lots.map((l) => ({ outer: [...l.corners], holes: [] })),
    ]
  );
  return {
    lots,
    reserved: [
      ...lots.map((l) => ({
        id: `surveyed:${l.reconstruction!.id}`,
        polygon: { outer: [...l.corners], holes: [] },
      })),
      ...dry.map((polygon, i) => ({ id: `sacraments-private-ground-${i}`, polygon })),
    ],
    unresolved: SACRAMENTS_PROPERTY_ROWS.flatMap((r) => r.houses.map((h) => h.id)).filter(
      (id) => !built.has(id)
    ),
  };
}
